/**
 * The price list, and the button that starts a purchase.
 *
 * Its own file because it shares nothing with the rest of the welcome page.
 * That page is a set of rooms built out of sprites; this is three fetches and a
 * list, and it sat in the middle of the scene builder for no reason other than
 * being on the same screen.
 *
 * The purchase itself is `checkoutUrl` in service.js, shared with the account
 * page. This page once had its own copy of that and it had already drifted.
 */

import { checkoutUrl } from "./service.js";

const $ = (id: string) => document.getElementById(id);

/**
 * The price list, priced by the server.
 *
 * The same table the account page reads and the server charges against. The
 * per-pack note is worked out from the credit count rather than written down,
 * so changing a pack cannot leave behind a sentence describing the old one.
 * Sixteen credits is roughly what one of the rooms above cost.
 */
/**
 * The button on a pack.
 *
 * Signed out it is a link to sign in rather than a button that works and then
 * fails: the account is what credits belong to, so there is nothing to buy
 * against yet, and finding that out after the card form is the wrong moment.
 *
 * Signed in, the click asks the server for a checkout URL and goes there. The
 * price is not sent, only the pack's name, because a price coming from a browser
 * is a price somebody can edit.
 */
export function buyControl(pack: { id: string; name: string }, signedIn: boolean): HTMLElement {
	if (!signedIn) {
		const link = document.createElement("a");
		link.className = "go small pack-buy";
		link.href = `/signin?next=pricing`;
		link.textContent = "Sign in to buy";
		return link;
	}

	const button = document.createElement("button");
	button.type = "button";
	button.className = "go small pack-buy";
	button.textContent = `Buy ${pack.name}`;

	button.addEventListener("click", async () => {
		// Disabled for the whole round trip, because two clicks on a slow
		// connection is two checkout sessions and a confused person.
		button.disabled = true;
		const was = button.textContent;
		button.textContent = "Opening…";
		try {
			// The shared one. This page had its own copy, and it had already drifted:
			// it read the reply as JSON without a guard, so a 502 that answered with
			// a page rather than an object failed with a parser error instead of the
			// sentence the copy was written to show.
			window.location.href = await checkoutUrl(pack.id);
		} catch (err) {
			button.disabled = false;
			button.textContent = was;
			const note = button.parentElement?.querySelector(".pack-err");
			const said = err instanceof Error ? err.message : "That could not be started.";
			if (note) note.textContent = said;
			else {
				const p = document.createElement("p");
				p.className = "pack-err";
				p.setAttribute("role", "status");
				p.textContent = said;
				button.parentElement?.append(p);
			}
		}
	});
	return button;
}

export async function fillPacks() {
	const list = $("packs");
	const free = $("freeCredits");
	if (!list) return;

	try {
		const [packs, config, me] = await Promise.all([
			fetch("/api/packs").then((r) => r.json()) as Promise<{
				packs: { id: string; name: string; credits: number; pence: number; each: string }[];
				open: boolean;
			}>,
			fetch("/api/config")
				.then((r) => r.json())
				.catch(() => ({}) as { freeLimit?: number }),
			// Whether to offer a button or a way to sign in. Failing this only costs
			// the visitor a sign-in page they were going to see anyway.
			fetch("/api/me")
				.then((r) => r.json())
				.catch(() => ({ signedIn: false }) as { signedIn?: boolean }),
		]);

		if (free && config.freeLimit) free.textContent = String(config.freeLimit);

		const money = (pence: number) => `£${(pence / 100).toFixed(2).replace(/.00$/, "")}`;
		// Bands rather than one sentence per pack, so two packs of very different
		// sizes cannot end up described identically and make the middle one look
		// pointless.
		const rooms = (credits: number) => {
			const n = Math.round(credits / 16);
			if (n <= 1) return "About a roomful of the things above.";
			if (n < 8) return `About ${n} rooms like the ones above.`;
			if (n < 30) return `Around ${n} rooms, or a small game's worth.`;
			return "A whole game's worth of art, with room to change your mind.";
		};

		// The placeholders have done their job the moment real prices exist.
		for (const ghost of list.querySelectorAll("[data-skeleton]")) ghost.remove();

		for (const [i, p] of packs.packs.entries()) {
			const li = document.createElement("li");
			li.className = "pack-card";
			// The middle one, which is what most people should buy.
			if (i === 1) li.classList.add("lit");
			li.append(
				Object.assign(document.createElement("p"), { className: "pack-name", textContent: p.name }),
				Object.assign(document.createElement("p"), { className: "pack-price", textContent: money(p.pence) }),
				Object.assign(document.createElement("p"), {
					className: "pack-each",
					textContent: `${p.credits} credits, ${p.each}`,
				}),
				Object.assign(document.createElement("p"), { className: "pack-note", textContent: rooms(p.credits) }),
			);
			if (packs.open) li.append(buyControl(p, Boolean(me.signedIn)));
			list.append(li);
		}
	} catch {
		// No prices rather than wrong prices. The free card is markup, so the
		// section still says the one thing that is true without a server.
		//
		// The placeholders go too: a row of grey bars that never fills is worse
		// than a single honest card, because it reads as a page still loading
		// rather than a page that could not reach its own price list.
		for (const ghost of list.querySelectorAll("[data-skeleton]")) ghost.remove();
	}
}
